import { trackEvent } from '@/lib/mixpanel';

const NORMAL_CLOSE_CODES = new Set([1000, 1001]);

const PONG_TIMEOUT_MS = 8_000;

type WsCloseType = 'expected_navigation' | 'network_offline' | 'unexpected';

let currentWsDealId: string | null = null;

export function setCurrentWsDealId(dealId: string | null): void {
  currentWsDealId = dealId;
}

type WsState = 'idle' | 'connected' | 'down';
type WsDropCloseType = Exclude<WsCloseType, 'expected_navigation'>;

interface WsAnalyticsState {
  state: WsState;
  connectedAt: number | null;
  disconnectedAt: number | null;
  incidentDealId: string | null;
  closeType: WsDropCloseType | null;
  closeCode: number | null;
  downCause: 'close' | 'stall' | null;
  pongTimeoutId: ReturnType<typeof setTimeout> | null;
}

const analyticsState: WsAnalyticsState = {
  state: 'idle',
  connectedAt: null,
  disconnectedAt: null,
  incidentDealId: null,
  closeType: null,
  closeCode: null,
  downCause: null,
  pongTimeoutId: null,
};

function now(): number {
  return Date.now();
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

function connectionAgeMs(): number {
  return analyticsState.connectedAt !== null
    ? Math.max(0, now() - analyticsState.connectedAt)
    : 0;
}

export function detectCloseType(closeEvent?: unknown): WsCloseType {
  if (typeof document !== 'undefined' && document.hidden) {
    return 'expected_navigation';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'network_offline';
  }
  const code = getCloseCode(closeEvent);
  if (code !== null && NORMAL_CLOSE_CODES.has(code)) {
    return 'expected_navigation';
  }
  return 'unexpected';
}

function getCloseCode(closeEvent?: unknown): number | null {
  if (
    closeEvent &&
    typeof closeEvent === 'object' &&
    'code' in closeEvent &&
    typeof (closeEvent as { code: unknown }).code === 'number'
  ) {
    return (closeEvent as { code: number }).code;
  }
  return null;
}

function clearPongTimeout(): void {
  if (analyticsState.pongTimeoutId !== null) {
    clearTimeout(analyticsState.pongTimeoutId);
    analyticsState.pongTimeoutId = null;
  }
}

function armPongTimeout(): void {
  if (analyticsState.pongTimeoutId !== null) return;
  analyticsState.pongTimeoutId = setTimeout(() => {
    analyticsState.pongTimeoutId = null;
    onPongTimeout();
  }, PONG_TIMEOUT_MS);
}

function onPongTimeout(): void {
  if (analyticsState.state !== 'connected') return;
  if (isHidden()) return;
  trackEvent('WEBSOCKET_STALLED', {
    dealId: currentWsDealId,
    connectionAgeMs: connectionAgeMs(),
    wasHidden: isHidden(),
    online: isOnline(),
  });
  analyticsState.state = 'down';
  analyticsState.disconnectedAt = now();
  analyticsState.incidentDealId = currentWsDealId;
  analyticsState.closeType = null;
  analyticsState.closeCode = null;
  analyticsState.downCause = 'stall';
}

function emitReconnected(): void {
  const downtimeMs =
    analyticsState.disconnectedAt !== null
      ? Math.max(0, now() - analyticsState.disconnectedAt)
      : 0;
  trackEvent('WEBSOCKET_RECONNECTED', {
    dealId: analyticsState.incidentDealId,
    downtimeMs,
    cause: analyticsState.downCause ?? 'close',
  });
  analyticsState.disconnectedAt = null;
  analyticsState.incidentDealId = null;
  analyticsState.closeType = null;
  analyticsState.closeCode = null;
  analyticsState.downCause = null;
}

export const wsAnalyticsListeners = {
  connecting(): void {
    clearPongTimeout();
  },

  connected(): void {
    clearPongTimeout();
    if (analyticsState.state === 'down') {
      emitReconnected();
    }
    analyticsState.state = 'connected';
    analyticsState.connectedAt = now();
  },

  closed(event: unknown): void {
    clearPongTimeout();
    if (analyticsState.state !== 'connected') return;

    const code = getCloseCode(event);
    if (code !== null && NORMAL_CLOSE_CODES.has(code)) {
      analyticsState.state = 'idle';
      return;
    }

    const closeType = detectCloseType(event);
    trackEvent('WEBSOCKET_DROPPED', {
      dealId: currentWsDealId,
      closeCode: code,
      closeType,
      connectionAgeMs: connectionAgeMs(),
      wasHidden: isHidden(),
      online: isOnline(),
    });

    if (closeType === 'expected_navigation') {
      analyticsState.state = 'idle';
      return;
    }

    analyticsState.state = 'down';
    analyticsState.disconnectedAt = now();
    analyticsState.incidentDealId = currentWsDealId;
    analyticsState.closeType = closeType;
    analyticsState.closeCode = code;
    analyticsState.downCause = 'close';
  },

  ping(received: boolean): void {
    if (received) return;
    if (analyticsState.state !== 'connected') return;
    armPongTimeout();
  },

  pong(received: boolean): void {
    if (!received) return;
    clearPongTimeout();

    if (
      analyticsState.state === 'down' &&
      analyticsState.downCause === 'stall'
    ) {
      emitReconnected();
      analyticsState.state = 'connected';
    }
  },
};

export function notifyUserAction(action: string): void {
  if (analyticsState.state !== 'down') return;
  if (currentWsDealId === null) return;
  if (currentWsDealId !== analyticsState.incidentDealId) return;

  const msSinceDisconnect =
    analyticsState.disconnectedAt !== null
      ? Math.max(0, now() - analyticsState.disconnectedAt)
      : 0;

  trackEvent('USER_ACTED_WHILE_WS_DISCONNECTED', {
    dealId: currentWsDealId,
    action,
    msSinceDisconnect,
    closeType: analyticsState.closeType,
    closeCode: analyticsState.closeCode,
  });
}

export function __resetWsAnalyticsForTest(): void {
  clearPongTimeout();
  analyticsState.state = 'idle';
  analyticsState.connectedAt = null;
  analyticsState.disconnectedAt = null;
  analyticsState.incidentDealId = null;
  analyticsState.closeType = null;
  analyticsState.closeCode = null;
  analyticsState.downCause = null;
  currentWsDealId = null;
}
